import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Radio, X } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ImageUpload } from '../../components/common/ImageUpload';
import { ShareButtons } from '../../components/common/ShareButtons';
import { SignalListRow } from '../../components/signals/SignalListRow';
import { SignalDetailModal } from '../../components/signals/SignalDetailModal';
import { SignalFilterBar, filterByDate, defaultDateFilter, DateFilter } from '../../components/signals/SignalFilterBar';
import { adminListSignals, adminCreateSignal, adminUpdateSignal, adminDeleteSignal, Signal, SignalPayload } from '../../features/signals/api/signals';

const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';
const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';

const empty: SignalPayload = { instrument: '', direction: 'buy', entry_price: 0, take_profit: null, stop_loss: null, note: '', image_url: '', image_link: '', audience: 'all', is_published: true };

export default function AdminSignalsPage() {
  const [items, setItems] = useState<Signal[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<SignalPayload>(empty);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState<Signal | null>(null);
  const [detail, setDetail] = useState<Signal | null>(null);
  const [filter, setFilter] = useState<DateFilter>(defaultDateFilter);
  const [q, setQ] = useState('');

  const load = () => { setLoading(true); adminListSignals().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const byDate = filterByDate(items, filter);
    const qq = q.trim().toLowerCase();
    return qq ? byDate.filter((s) => s.instrument.toLowerCase().includes(qq)) : byDate;
  }, [items, filter, q]);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (s: Signal) => {
    setEditId(s.id);
    setForm({ instrument: s.instrument, direction: s.direction, entry_price: s.entry_price, take_profit: s.take_profit ?? null, stop_loss: s.stop_loss ?? null, note: s.note || '', image_url: s.image_url || '', image_link: s.image_link || '', audience: s.audience, is_published: s.is_published });
    setOpen(true);
  };

  const save = async () => {
    if (!form.instrument.trim()) return toast.error('Instrument wajib diisi');
    if (!(Number(form.entry_price) > 0)) return toast.error('Entry price wajib diisi');
    setSaving(true);
    try {
      if (editId) await adminUpdateSignal(editId, form);
      else await adminCreateSignal(form);
      toast.success(editId ? 'Signal diperbarui' : 'Signal dibuat');
      setOpen(false); load();
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!del) return;
    try { await adminDeleteSignal(del.id); toast.success('Signal dihapus'); setDel(null); load(); }
    catch (e: any) { toast.error('Gagal hapus', { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Signal & Mapping">
      <div className="max-w-4xl" data-testid="admin-signals-page">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><Radio className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
            <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Signal & Mapping</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Kelola Signal ({items.length})</h2></div>
          </div>
          <button data-testid="signal-new-btn" onClick={openNew} className="bg-cyan-400 text-[#050914] font-medium px-4 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors flex items-center gap-2"><Plus className="w-4 h-4" strokeWidth={2} /> Signal Baru</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : items.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 text-center text-white/50">Belum ada signal. Klik "Signal Baru".</div>
        ) : (
          <>
            <SignalFilterBar value={filter} onChange={setFilter} search={q} onSearch={setQ} />
            {filtered.length === 0 ? (
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 text-center text-white/50" data-testid="signal-empty-filtered">Tidak ada signal pada rentang tanggal ini.</div>
            ) : (
              <div className="space-y-3" data-testid="signal-list">
                {filtered.map((s) => (
                  <SignalListRow key={s.id} s={s} testId={`signal-row-${s.id}`} onClick={() => setDetail(s)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {detail && (
        <SignalDetailModal signal={detail} onClose={() => setDetail(null)}>
          <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <span className={`px-2 py-0.5 rounded-full border ${detail.is_published ? 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10' : 'text-white/50 border-white/15 bg-white/5'}`}>{detail.is_published ? 'Published' : 'Draft'}</span>
              <span className="text-white/40">Audience: {detail.audience === 'vip' ? 'VIP' : 'Semua Member'}</span>
              <div className="ml-auto flex gap-1">
                <button data-testid={`signal-edit-${detail.id}`} onClick={() => { const d = detail; setDetail(null); openEdit(d); }} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                <button data-testid={`signal-delete-${detail.id}`} onClick={() => { setDel(detail); setDetail(null); }} className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>
            <ShareButtons url={`${process.env.REACT_APP_BACKEND_URL}/api/v1/public/signals/${detail.id}/share`} text={`Signal ${detail.instrument} ${detail.direction.toUpperCase()} @ ${detail.entry_price}`} testIdPrefix={`admin-signal-share-${detail.id}`} />
          </div>
        </SignalDetailModal>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative bg-[#0a1122] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" data-testid="signal-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">{editId ? 'Edit Signal' : 'Signal Baru'}</h3>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Instrument *</label>
                  <input data-testid="signal-instrument" className={inputClass} placeholder="XAUUSD" value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} />
                </div>
                <div>
                  <label className={labelClass}>Arah *</label>
                  <select data-testid="signal-direction" className={inputClass} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value as 'buy' | 'sell' })}>
                    <option value="buy" className="bg-[#050914]">BUY</option>
                    <option value="sell" className="bg-[#050914]">SELL</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className={labelClass}>Entry *</label><input data-testid="signal-entry" type="number" step="any" className={inputClass} value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: Number(e.target.value) })} /></div>
                <div><label className={labelClass}>TP</label><input type="number" step="any" className={inputClass} value={form.take_profit ?? ''} onChange={(e) => setForm({ ...form, take_profit: e.target.value === '' ? null : Number(e.target.value) })} /></div>
                <div><label className={labelClass}>SL</label><input type="number" step="any" className={inputClass} value={form.stop_loss ?? ''} onChange={(e) => setForm({ ...form, stop_loss: e.target.value === '' ? null : Number(e.target.value) })} /></div>
              </div>
              <div>
                <label className={labelClass}>Audience</label>
                <select data-testid="signal-audience" className={inputClass} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value as 'all' | 'vip' })}>
                  <option value="all" className="bg-[#050914]">Semua Member (Umum)</option>
                  <option value="vip" className="bg-[#050914]">Khusus VIP</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Catatan</label>
                <textarea className={inputClass} rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Analisa / mapping singkat..." />
              </div>
              <div>
                <label className={labelClass}>Gambar Chart</label>
                <ImageUpload value={form.image_url} onUploaded={(url) => setForm({ ...form, image_url: url })} label="Unggah Chart" testId="signal-image-upload" />
                <div className="mt-2">
                  <label className="block text-[11px] text-white/40 mb-1">Atau tempel link gambar dari TradingView</label>
                  <input
                    data-testid="signal-image-link"
                    className={inputClass}
                    placeholder="https://www.tradingview.com/x/XXXX/"
                    value={form.image_link || ''}
                    onChange={(e) => setForm({ ...form, image_link: e.target.value })}
                  />
                  <p className="text-[11px] text-white/40 mt-1">Di TradingView: klik ikon kamera → "Copy link to the chart image", lalu tempel di sini.</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="accent-cyan-400" /> Publikasikan
              </label>
              <button data-testid="signal-save-btn" onClick={save} disabled={saving} className="w-full bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={confirmDelete} title="Hapus Signal?" description={`Signal ${del?.instrument} akan dihapus permanen.`} confirmLabel="Hapus" />
    </DashboardLayout>
  );
}
